import { Menu, MenuItem, SxProps, Theme } from '@mui/material'
import { UserStatusEnum, useUserStatusOptions, useUserStatusColors } from '@data/users/user'
import React, { useState } from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { CaslCanNotHide } from '@/contexts/CaslProvider'
import { Button } from '@/@core/components/mui/Button'

interface UserStatusDropDownProps {
  status: UserStatusEnum
  onStatusChange?: (status: UserStatusEnum) => void
  onSelect?: (status: UserStatusEnum) => void
  disabled?: boolean
  className?: string
  fullWidth?: boolean
  sx?: SxProps<Theme>
  isDetail?: boolean
  excludeGuestStatuses?: boolean
  readonly?: boolean
  statusPrevious?: UserStatusEnum
}

const UserStatusDropDown = ({
  status,
  onStatusChange,
  onSelect,
  fullWidth,
  disabled = false,
  readonly = false,
  className = '',
  sx,
  isDetail = false,
  excludeGuestStatuses = false,
  statusPrevious
}: UserStatusDropDownProps) => {
  const statusOptions = useUserStatusOptions()
  const statusColors = useUserStatusColors()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDetail || readonly || status === UserStatusEnum.WAITING_DELETE) return
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (selectedStatus: UserStatusEnum) => {
    onStatusChange?.(selectedStatus)
    onSelect?.(selectedStatus)
    handleClose()
  }

  const selectedOption = statusOptions.find(option => option.value === status)

  // Filter out guest statuses if excludeGuestStatuses is true
  let filteredOptions = excludeGuestStatuses
    ? statusOptions.filter(
        option =>
          ![UserStatusEnum.GUEST, UserStatusEnum.GUEST_WAITING, UserStatusEnum.WAITING_DELETE].includes(option.value)
      )
    : statusOptions

  // Special case: if status is blocked and previous status was guest or guest waiting, only show Guest Waiting option
  if (
    status === UserStatusEnum.BLOCKED &&
    (statusPrevious === UserStatusEnum.GUEST ||
      statusPrevious === UserStatusEnum.GUEST_WAITING ||
      statusPrevious === UserStatusEnum.WAITING_DELETE)
  ) {
    filteredOptions = statusOptions.filter(option => option.value === UserStatusEnum.GUEST_WAITING)
  }

  return (
    <CaslCanNotHide action='put' subject='lift_list'>
      {(allowed: boolean) => (
        <>
          <Button
            onClick={allowed ? handleClick : undefined}
            disabled={disabled || readonly}
            endIcon={
              allowed && !(isDetail || readonly) && status !== UserStatusEnum.WAITING_DELETE ? (
                <KeyboardArrowDownIcon />
              ) : null
            }
            className={className}
            variant='tonal'
            fullWidth={fullWidth}
            color={selectedOption ? statusColors[selectedOption.value] : undefined}
            sx={sx}
          >
            <span className='font-medium'>{selectedOption?.label}</span>
          </Button>

          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            {filteredOptions
              .filter(option => ![UserStatusEnum.WAITING].includes(option.value))
              .map(option => (
                <MenuItem
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  sx={{ color: `${statusColors[option.value]}.main` }}
                >
                  {option.label}
                </MenuItem>
              ))}
          </Menu>
        </>
      )}
    </CaslCanNotHide>
  )
}

export default UserStatusDropDown
